import { Injectable } from '@angular/core';
import { NgxMatNativeDateAdapter } from '@angular-material-components/datetime-picker';
import { DateTime } from 'luxon';

/**
 * Datetime-picker adapter that keeps the working value as a native JS `Date`
 * (so form controls emit `Date`, not a wrapper object) but uses Luxon for
 * parsing and display formatting. This lets us drop `moment` + the
 * `moment-adapter` entirely while preserving the exact picker text format.
 *
 * Display/parse format strings live in `LUXON_NGX_DATETIME_FORMATS` below and
 * use Luxon tokens (see https://moment.github.io/luxon/#/formatting).
 */
@Injectable()
export class LuxonNgxDateAdapter extends NgxMatNativeDateAdapter {
  /** Parse a user-typed string using the configured Luxon format, with graceful fallbacks. */
  override parse(value: any, parseFormat?: string | string[]): Date | null {
    if (value == null || value === '') {
      return null;
    }
    if (value instanceof Date) {
      return new Date(value.getTime());
    }
    if (typeof value === 'number') {
      return new Date(value);
    }
    const str = String(value).trim();

    // Try the configured parse format(s) first.
    const formats = Array.isArray(parseFormat)
      ? parseFormat
      : parseFormat
        ? [parseFormat]
        : [LUXON_NGX_DATETIME_FORMATS.parse.dateInput as string];

    for (const fmt of formats) {
      const dt = DateTime.fromFormat(str, fmt, { locale: this.locale });
      if (dt.isValid) {
        return dt.toJSDate();
      }
    }

    // Fall back to ISO, then to the native Date parser (matches old moment leniency).
    const iso = DateTime.fromISO(str, { locale: this.locale });
    if (iso.isValid) {
      return iso.toJSDate();
    }
    const native = new Date(str);
    return isNaN(native.getTime()) ? null : native;
  }

  /** Format a Date for display using the Luxon token in `displayFormat`. */
  override format(date: Date, displayFormat: any): string {
    if (!this.isValid(date)) {
      return '';
    }
    const fmt =
      typeof displayFormat === 'string'
        ? displayFormat
        : (LUXON_NGX_DATETIME_FORMATS.display.dateInput as string);
    return DateTime.fromJSDate(date).setLocale(this.locale).toFormat(fmt);
  }
}

/**
 * Picker format config, expressed in Luxon tokens. These mirror the previous
 * moment tokens ('DD MMMM YYYY, hh:mm A') so the displayed text is unchanged:
 *   DD  -> dd    (zero-padded day)
 *   MMMM-> MMMM  (full month name — same token)
 *   YYYY-> yyyy  (4-digit year)
 *   hh  -> hh    (12-hour, zero-padded)
 *   mm  -> mm    (minutes)
 *   A   -> a     (AM/PM)
 */
export const LUXON_NGX_DATETIME_FORMATS = {
  parse: {
    dateInput: 'dd MMMM yyyy, hh:mm a',
  },
  display: {
    dateInput: 'dd MMMM yyyy, hh:mm a',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'DD',
    monthYearA11yLabel: 'MMMM yyyy',
  },
};
